/*
 * Ported from town-square so the reputation popover reads identically in both apps — see
 * docs/decisions/0002-shared-react-components.md for why this is duplicated rather than shared.
 * The one deliberate difference: community posts live on town-square, so this app links out
 * through `buildTownSquareUrl` instead of routing in-app.
 */
import { buildTownSquareUrl } from '../../utils';
import { formatSignedDelta } from './tween-number';
import { relTime, type TFn } from './rel-time';
import type { ReputationSource } from './reputation-queries';

interface ReputationSourceRowProps {
  node: ReputationSource;
  t: TFn;
  /** Popover variant: tighter row, no meta line. */
  compact?: boolean;
}

function referralName(node: ReputationSource): string {
  const u = node.referralUser;
  if (!u) return '';
  if (u.profile?.displayName?.trim()) return u.profile.displayName.trim();
  const real = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  if (real) return real;
  return u.username ?? '';
}

function label(node: ReputationSource, t: TFn): string {
  if (node.bucket === 'post') {
    if (!node.post) return t('points.sources-post-deleted');
    // The API borrows an untitled post's first sentence; only a post with no words is blank.
    return node.post.title || t('points.sources-post-untitled');
  }
  if (node.bucket === 'comment') {
    if (!node.post) return t('points.sources-comment-deleted');
    if (!node.post.title) return t('points.sources-comment-on-untitled');
    return t('points.sources-comment-on', { title: node.post.title });
  }
  if (node.bucket === 'referral') {
    const name = referralName(node);
    return name ? t('points.sources-referral', { name }) : t('points.sources-referral-unknown');
  }
  if (node.bucket === 'quest') {
    return t('points.sources-quest');
  }
  if (node.bucket === 'first-touch') {
    return t('points.sources-first-touch');
  }
  if (node.bucket === 'bounty') {
    return node.bountyName ? t('points.sources-bounty-named', { name: node.bountyName }) : t('points.sources-bounty');
  }
  return t('points.sources-initial');
}

export function ReputationSourceRow({ node, t, compact = false }: ReputationSourceRowProps) {
  const text = label(node, t);
  const deleted = node.post?.isDeleted ?? false;
  const linkable = node.post && !deleted;

  return (
    <div className={compact ? 'rep-source-row is-compact' : 'rep-source-row'}>
      <div className="rep-source-delta">{formatSignedDelta(node.netPoints)}</div>
      <div className="rep-source-main">
        {linkable ? (
          <a
            className="rep-source-title"
            href={buildTownSquareUrl(`/community/${node.post!.communitySlug}/post/${node.post!.slug}`)}
          >
            {text}
          </a>
        ) : (
          <span className="rep-source-title">
            {text}
            {deleted && <span className="rep-source-deleted"> {t('points.sources-deleted-suffix')}</span>}
          </span>
        )}
        {!compact && (
          <div className="rep-source-meta">
            {t('points.sources-events', { count: node.eventCount })} · {relTime(node.lastEarnedAt, t)}
          </div>
        )}
      </div>
    </div>
  );
}
